<template>
  <div class="system-card-container">
    <div class="card-grid" ref="cardGridRef">
      <!-- 新增系统按钮卡片 -->
      <div class="add-card" :style="{ width: `${cardBaseSize.width}px`, height: `${cardBaseSize.height}px` }" @click="handleAddSystem">
        <div class="add-icon">+</div>
        <div class="add-text">新增系统</div>
      </div>

      <SystemCardItem
        v-for="(system, index) in systemList"
        :key="system.GUID"
        :style="{ width: `${cardBaseSize.width}px`, height: `${cardBaseSize.height}px` }"
        :system="system"
        :index="systemList.findIndex((item) => item.GUID === system.GUID)"
        :card-width="cardBaseSize.width"
        :card-height="cardBaseSize.height"
        @update-system="handleUpdateSystem"
        @delete-system="handleDeleteSystem"
        @update-position="handleUpdatePosition"
        @update-long-press-status="handleLongPressStatus"
        @update-status="handleSystemStatusUpdate"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed ,onMounted,onUnmounted } from 'vue'
import Sortable from 'sortablejs'
import SystemCardItem from './SystemCardItem.vue'
import { PieChartOutlined, AlertOutlined, SettingOutlined, LaptopOutlined } from '@ant-design/icons-vue'
// import { i } from 'vite/dist/node/types.d-aGj9QkWt'

// 卡片网格容器Ref（用于绑定Sortable拖拽）
const cardGridRef = ref<HTMLDivElement>(null)

// 1. 定义全局统一的卡片基础尺寸（响应式，后续可动态修改）
const cardBaseSize = ref({
  width: 300, // 统一宽度
  height: 180 // 统一高度
});

// Sortable拖拽禁用状态（由子组件长按事件控制）
const isSortableDisabled = ref(false)
const handleLongPressStatus = (status: boolean) => {
  isSortableDisabled.value = status
}

// 系统卡片数据列表
const systemList = ref([
  {
    GUID: 'a3e72c9d-8b1f-4672-9c5a-1d8f3c4e7b2d',
    name: '测试A',
    url: '/operation-analytics',
    status: 'enabled',
    updateTime: '2025-05-18',
       ImageUrl:'edit',
    isEditing: false,
    x: 0,
    y: 0
  },
  {
    GUID: '7f2d4e8b-3c6a-419f-8d2b-5e7c9a1d3f5b',
    name: '测试B',
    url: '/emr-quality',
    status: 'enabled',
    updateTime: '2025-05-15',
    isEditing: false,
       ImageUrl:'system',
    x: 0,
    y: 0
  },
  {
    GUID: 'd1b3f57a-2c4e-489d-7b6a-3f1c9e5d8b2a',
    name: '测试C',
    url: '/system/permission',
    status: 'enabled',
    updateTime: '2025-05-10',
    isEditing: false,
       ImageUrl:'edit',
    x: 0,
    y: 0
  },
  {
    GUID: '8c6e2a4d-9f1b-4357-8a2d-6e9c1f3b5d7a',
    name: '测试D',
    url: '/local/device-management',
    status: 'disabled',
    updateTime: '2025-05-20',
    isEditing: false,
    ImageUrl:'edit',
    x: 0,
    y: 0
  },
  {
    GUID: '3b5d7f9a-4c2e-416d-8b3f-5a7c9e2d4f6b',
    name: '测试E',
    url: '/prescription-check',
    status: 'enabled',
    updateTime: '2025-05-12',
    isEditing: false,
       ImageUrl:'edit',
    x: 0,
    y: 0
  },
  {
    GUID: 'e2f4a6c8-9d1b-473f-8a5d-6e2c9b1f7d3a',
    name: '测试F',
    url: '/dept/schedule',
    status: 'enabled',
    updateTime: '2025-05-16',
       ImageUrl:'edit',
    isEditing: false,
    x: 0,
    y: 0
  }
])

// 处理子组件传递的系统状态更新（启用/禁用）
const handleSystemStatusUpdate = (guid: string, newStatus: 'enabled' | 'disabled') => {
  const targetSystem = systemList.value.find(item => item.GUID === guid)
  if (targetSystem) {
    targetSystem.status = newStatus
  }
}
const sortableInstance = ref<Sortable | null>(null);
onUnmounted(() => {
  window.removeEventListener('resize', setInitialCardSize);
});
// 挂载时初始化Sortable拖拽
onMounted(() => {
  setInitialCardSize();
  window.addEventListener('resize', setInitialCardSize); // 监听屏幕变化  
  if(isSortableDisabled.value){
    return
  }
  if (cardGridRef.value) {
    const sortable = new Sortable(cardGridRef.value, {
      animation: 300,
      easing: 'cubic-bezier(0.2, 1, 0.3, 1)',
      touchStartThreshold: 10,
      filter: '.add-card', // 过滤新增卡，不允许拖拽新增卡
      
      // 生成拖拽克隆体
      clone: (original) => {
        const clone = original.cloneNode(true)
        clone.classList.add('sortable-clone')
        return clone
      },
      
      // 拖拽相关样式类
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      
      // 拖拽开始：判断是否取消拖拽
      onStart: (evt) => {
        // 长按状态下取消拖拽
        if (isSortableDisabled.value) {
          return
        }

        // 非编辑态的卡片内部点击，取消拖拽
        const isClickOnCardItem = evt.item?.querySelector('.system-card')
        const isCardEditing = isClickOnCardItem?.querySelector('.ant-input:not(.input-readonly)')
        if (isClickOnCardItem && !isCardEditing) {
          sortable.cancel()
        }
      },
      
      // 拖拽结束：更新卡片列表顺序
      onEnd: (evt) => {
        const oldIndex = evt.oldIndex - 1
        const newIndex = evt.newIndex - 1
        
        // 边界判断：排除新增卡的索引影响
        if (oldIndex < 0 || oldIndex >= systemList.value.length) return
        if (newIndex < 0 || newIndex >= systemList.value.length) return
        
        // 调整数组顺序，同步视图与数据
        const [movedItem] = systemList.value.splice(oldIndex, 1)
        systemList.value.splice(newIndex, 0, movedItem)
        
        // 重置卡片位置坐标
        systemList.value.forEach((item) => {
          item.x = 0
          item.y = 0
        })
        systemList.value = [...systemList.value]
      },
      
      // 拖拽过程：控制占位符显示动画
      onMove: (evt) => {
        const addCard = evt.related?.closest('.add-card'); // 找到“即将插入位置”附近的新增卡片
          // 两种非法场景：
          // a. 即将插入的位置是新增卡片的上方/下方（related 是新增卡片）
          // b. 拖拽的元素是系统卡片（不是新增卡片），且插入位置涉及新增卡片
          if (addCard && !evt.dragged.classList.contains('add-card')) {
            return false; // 返回false，阻止插入到新增卡片位置
          }        
        const ghostEl = evt.related
        if (ghostEl && ghostEl.classList.contains('sortable-ghost')) {
          ghostEl.classList.remove('sortable-ghost-visible')
          setTimeout(() => {
            ghostEl.classList.add('sortable-ghost-visible')
          }, 100)
        }
        return true
      }
    })
    sortableInstance.value = sortable; // 保存实例
  }
})

// 3. 可新增手动控制的方法（比如按钮调用）
const toggleSortable = (enabled: boolean) => {
  if (sortableInstance.value) {
    if (enabled) {
      sortableInstance.value.option('disabled', false); // 启用拖拽
    } else {
      sortableInstance.value.option('disabled', true); // 禁用拖拽（等同于设置isSortableDisabled）
    }
  }
}

// 新增系统卡片
const handleAddSystem = () => {
  const newSystem = {
    GUID: `new-${Date.now()}`,
    name: '未命名系统',
    url: 'http://',
    status: 'enabled',
    ImageUrl:'default',
    updateTime: new Date().toLocaleDateString().replace(/\//g, '-'),
    isEditing: true,
  }
  toggleSortable(false)
  systemList.value.unshift(newSystem)
}

// 更新系统卡片信息（编辑后保存）
const handleUpdateSystem = (guid: string, updateData: Partial<(typeof systemList.value)[0]>) => {
  // 修改时禁止拖拽
  toggleSortable(!updateData.isEditing);
  const index = systemList.value.findIndex((item) => item.GUID === guid)
  if (index !== -1) {
    systemList.value[index] = { ...systemList.value[index], ...updateData }
  }
}

// 删除系统卡片
const handleDeleteSystem = (guid: string) => {
  const index = systemList.value.findIndex((item) => item.GUID === guid)
  if (index !== -1) {
    systemList.value.splice(index, 1)
  }
}

// 更新系统卡片位置坐标
const handleUpdatePosition = (guid: string, newX: number, newY: number) => {
  const index = systemList.value.findIndex((item) => item.GUID === guid)
  if (index !== -1) {
    // 限制卡片不超出屏幕可视区
    const maxX = window.innerWidth - systemList.value[index].width
    const maxY = window.innerHeight - systemList.value[index].height
    systemList.value[index].x = Math.max(0, Math.min(newX, maxX))
    systemList.value[index].y = Math.max(0, Math.min(newY, maxY))
  }
}

// 1. 初始化时根据屏幕宽度设置初始尺寸
const setInitialCardSize = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth >= 3840) { 
    // 4K 超高清屏（常见：3840×2160，如带鱼屏、专业显示器）
    cardBaseSize.value = { width: 480, height: 260 }; // 更大卡片，适配大屏视野
  } else if (screenWidth >= 2560) { 
    // 3K/2.5K 大屏（常见：2560×1440、2560×1080 带鱼屏）
    cardBaseSize.value = { width: 370, height: 200 }; // 次大屏尺寸，平衡显示数量与清晰度
  } else if (screenWidth >= 1920) { 
    // 1080P 全高清屏（主流：1920×1080，台式机/笔记本通用）
    cardBaseSize.value = { width: 410, height: 200 }; // 原大屏尺寸，适配1080P视野
  } else if (screenWidth >= 1366) { 
    // 中屏（常见：1366×768 笔记本、1440×900 老旧显示器）
    cardBaseSize.value = { width: 290, height: 180 }; // 原中屏尺寸，保证单屏显示数量
  } else if (screenWidth >= 1024) { 
    // 1K 小屏（常见：1024×768 平板、老旧笔记本/显示器）
    cardBaseSize.value = { width: 260, height: 160 }; // 缩小尺寸，避免卡片溢出屏幕
  } else { 
    // 超小屏（手机横屏、小尺寸平板：<1024px）
    cardBaseSize.value = { width: 220, height: 140 }; // 最小尺寸，优先保证单屏能显示2列
  }
};

const cardStyle = computed(() => ({
  width: `${props.cardWidth}px`,
  height: `${props.cardHeight}px`,
  // ... 其他样式
}));
</script>

<style scoped>
.system-card-container {
  padding: 24px;
  background-color: #f5f5f5;
}

.card-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  max-width: 99.5vw;
  margin: 0 auto;
  padding: 10px;
}

/* 新增卡片样式 */
.add-card {
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
    padding: 0px 0; /* 用上下 padding 替代 .add-icon 的 padding-top，让布局更均匀 */
  overflow: visible; /* 避免子元素被截断 */
  
}

.add-card:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
}

.add-icon {
  font-size: 64px;
  color: #999;
  padding-top: 50px;
  line-height: 10px;
  transition: color 0.2s ease;
}

.add-icon:hover {
  color: #1890ff;
  transform: scale(1.05);
  transition: all 0.2s ease;
}

.add-text {
  font-size: 16px;
  color: #666;
  padding: 0;
  margin: 0;
  line-height: 90px!important;
}

/* 卡片容器基础样式（统一尺寸） */
.card-grid > div {
  /* width: 300px;
  height: 180px; */
  transition: all 0.3s ease;
}

/* 原卡片被选中拖拽时的样式 */
:deep(.sortable-chosen) {
  opacity: 0.5;
}

/* 隐藏占位符内的内容，避免干扰视觉 */
:deep(.sortable-ghost *),
:deep(.sortable-ghost .system-icon),
:deep(.sortable-ghost .edit-name-input) {
  display: none !important;
}

/* 拖拽克隆体样式 */
:deep(.sortable-clone) {
  opacity: 0.6;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transform: scale(1.05);
  border: 2px solid #1890ff;
  pointer-events: none;
  z-index: 1000;
  transition: transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}

/* 系统卡片过渡效果 */
:deep(.system-card) {
  transition: all 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}

/* 拖拽占位符样式 */
:deep(.sortable-ghost) {
  background-color: #f0f0f0;
  border: 2px dashed #1890ff;
  width: 300px;
  height: 180px;
  visibility: visible !important;
  transition: opacity 0.2s ease, transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
  opacity: 80;
  /* opacity: 0; */
}

/* 占位符显示动画 */
:deep(.sortable-ghost.sortable-ghost-visible) {
  opacity: 1;
  transform: scale(1.02);
}

/* 拖拽中隐藏原卡片 */
:deep(.sortable-chosen.sortable-dragging) {
  visibility: hidden;
  opacity: 0;
}
</style>